import React, { createContext, useContext } from 'react';
import { useAuth as useAuthHook, type UseAuthReturn, type AuthService } from '@textile-erp/ui';
import { createApiClient, buildBackendUrl, clearStoredMenus } from '@textile-erp/core';

// Create core API client for authentication
const coreApiClient = createApiClient(buildBackendUrl('/api'));

// Setup automatic logout on 401 responses
let isLoggingOut = false;
coreApiClient.setOnUnauthorized(() => {
  // Will be set later when AuthProvider mounts
});

// Configurable auth service using core API client
const authService: AuthService = {
  login: async (credentials) => {
    try {
      const response: any = await coreApiClient.post('/auth/login', credentials);
      const { user, token } = response;
      
      return {
        user,
        token
      };
    } catch (error: any) {
      throw new Error(error.message || 'Login failed');
    }
  },
  
  logout: async () => {
    try {
      await coreApiClient.post('/auth/logout');
    } catch (error) {
      // Logout should succeed even if API call fails
      console.warn('Logout API call failed, but continuing with local logout');
    } finally {
      // Always clear the token from API client
      coreApiClient.clearAuthToken();
      // Clear cached menus
      clearStoredMenus();
    }
  },
  
  getCurrentUser: async () => {
    try {
      const user = await coreApiClient.get('/auth/me') as any;
      return user;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to get current user');
    }
  },
  
  verifyToken: async (token: string) => {
    try {
      coreApiClient.setAuthToken(token);
      const user = await coreApiClient.get('/auth/me') as any;
      return user;
    } catch (error) {
      throw new Error('Token verification failed');
    }
  }
};

const AuthContext = createContext<UseAuthReturn | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuthHook({
    authService,
    tokenKey: 'textile_erp_token',
    autoRefresh: true,
    refreshInterval: 15 * 60 * 1000, // 15 minutes
  });

  // Synchronize API client token with localStorage
  React.useEffect(() => {
    const token = localStorage.getItem('textile_erp_token');
    
    if (token && auth.isAuthenticated) {
      coreApiClient.setAuthToken(token);
    } else if (!auth.isAuthenticated) {
      coreApiClient.clearAuthToken();
    }
  }, [auth.isAuthenticated]);

  // Set up automatic logout on 401 responses with loop prevention
  React.useEffect(() => {
    coreApiClient.setOnUnauthorized(() => {
      // Prevent infinite logout loops
      if (!isLoggingOut && auth.user && auth.logout) {
        isLoggingOut = true;
        auth.logout().finally(() => {
          isLoggingOut = false;
        });
      }
    });
  }, [auth.logout, auth.user]);

  return (
    <AuthContext.Provider value={auth}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): UseAuthReturn {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}